/**
 * Busca do plano do fabricante em SEGUNDO PLANO — os shapes do fio.
 *
 * Contrato congelado 31/08/2026, §3.1 e §3.2. Nada aqui se inventa: os três
 * avisos abaixo são exatamente o que o motor publica por Redis para todas as
 * conexões daquele usuário naquele inquilino.
 *
 * DUAS REGRAS DE SHAPE QUE NÃO SE QUEBRAM:
 *  - `tenantId` vem nos TRÊS avisos, e a tela descarta o de outro inquilino
 *    ANTES de tocar no estado (é o que os vizinhos já fazem em connectionStore).
 *  - a chave do acervo (`planKey`) NUNCA sai para a tela: ela é derivada de
 *    fabricante+modelo e é IGUAL entre inquilinos diferentes, então usá-la como
 *    identificador do trabalho misturaria clientes. O identificador é o `jobId`,
 *    um por (inquilino, conversa, equipamento, submissão).
 */

import type { ManualIngestRun } from '@rochadenis/axiomgo-contracts'

/** Motivo do fim sem plano do fabricante. */
export type PlanResearchFailureReason = 'ceiling' | 'build_failed' | 'unavailable'

/** Andamento — no MÁXIMO a cada 15s. */
export interface PlanResearchProgressEvent {
  type: 'plan.research.progress'
  jobId: string
  tenantId: string
  equipmentId: string
  conversationId: string
  /** O que o usuário lê no aviso, ex.: "Atlas Copco GA37". */
  label: string
  elapsedMs: number
  ceilingMs: number
  /** Opcional; vem do acervo quando houver. */
  stage?: string
}

/** O plano do fabricante chegou. */
export interface PlanResearchReadyEvent {
  type: 'plan.research.ready'
  jobId: string
  tenantId: string
  equipmentId: string
  conversationId: string
  label: string
  researchId: string
  /** true = o usuário já confirmou o provisório; a tela NÃO pode sobrescrever. */
  conflict: boolean
}

/** Não veio — e só sai quando NÃO há mais tentativa viva (contrato §2.6). */
export interface PlanResearchFailedEvent {
  type: 'plan.research.failed'
  jobId: string
  tenantId: string
  equipmentId: string
  conversationId: string
  label: string
  reason: PlanResearchFailureReason
}

/** Uma linha de `GET /api/v1/research/jobs/active` (contrato §3.2). */
export interface ResearchJobActiveRow {
  jobId: string
  equipmentId: string
  conversationId: string
  label: string
  /** ISO-8601. */
  startedAt: string
  ceilingMs: number
  status: 'running' | 'ready' | 'failed'
  researchId: string | null
  acknowledgedAt: string | null
}

export interface ResearchJobsActiveResponse {
  jobs: AnyActiveJobRow[]
  /** Este usuário pode excluir manual (Owner/Admin). Ausente = não. */
  canDeleteManual?: boolean
  manualDeletions?: ManualDeletionRow[]
}

/**
 * Manual grande (fatia 6 do motor, 2026-10-29): o job de leitura do manual
 * entra na MESMA lista `GET /api/v1/research/jobs/active`, marcado com
 * `kind: 'manual_ingest'` e contagens reais de página. A linha da pesquisa do
 * plano NÃO carrega `kind`. Espelha `ManualIngestJobView` do motor.
 */
export type ManualIngestStage =
  | 'queued'
  | 'reading'
  | 'ocr'
  | 'indexing'
  | 'structuring'
  | 'ready'
  | 'failed_retryable'
  | 'failed'
  | 'cancelled'
  /** Trava de segurança do mês estourada: o job espera "Continuar" ou "Parar" (motor M5b). */
  | 'paused_decision'

export interface ManualIngestActiveRow extends Omit<ResearchJobActiveRow, 'researchId'> {
  kind: 'manual_ingest'
  /** Sempre null: o job do manual não tem pesquisa do plano. */
  researchId: null
  /** `label` é o nome do arquivo. O estado fino vem em `progress.stage`; `status` o resume. */
  progress: {
    stage: ManualIngestStage
    pagesTotal: number
    pagesDone: number
    pagesOcr: number
    pagesIllegible: number
  }
  /** M3: leituras à frente (0 = o próximo). Só em `queued`. */
  queuePosition?: number
  /** M6: presente quando o job é a RODADA de vários manuais — uma entrada por rodada, uma linha por arquivo. */
  manualIngestRun?: ManualIngestRun
  /** O arquivo do manual no motor: é o que a ação "Excluir manual" endereça. */
  fileId?: string
}

/** Exclusão de manual pedida por uma pessoa nas últimas 24 h (`manualDeletions` de `/jobs/active`): a ficha fica, só leitura. */
export interface ManualDeletionRow {
  fileId: string
  fileName: string
  /** ISO-8601. */
  deletedAt: string
  deletedByName: string | null
  conversationIds: string[]
}

/** Qualquer linha que `/jobs/active` pode devolver. */
export type AnyActiveJobRow = ResearchJobActiveRow | ManualIngestActiveRow

export function isManualIngestRow(row: AnyActiveJobRow): row is ManualIngestActiveRow {
  return (row as { kind?: unknown }).kind === 'manual_ingest'
}
